import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { GeofenceInput, GeofenceGeometry, GeofenceType } from "@/api/v1/geofence-api";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";

// Define the form schema with Zod
const geofenceFormSchema = z.object({
  name: z.string().min(3, {
    message: "Geofence name must be at least 3 characters.",
  }),
  description: z.string().optional(),
});

// Infer the form values type from the schema
type GeofenceFormValues = z.infer<typeof geofenceFormSchema>;

interface GeofenceFormProps {
  onSubmit: (data: GeofenceInput) => void;
  geofenceGeometry: GeofenceGeometry;
  geometryType: GeofenceType;
  initialValues?: Partial<GeofenceFormValues>;
  isSubmitting?: boolean;
}

export function GeofenceForm({
  onSubmit,
  geofenceGeometry,
  geometryType,
  initialValues,
  isSubmitting = false,
}: GeofenceFormProps) {
  // Initialize the form with react-hook-form
  const form = useForm<GeofenceFormValues>({
    resolver: zodResolver(geofenceFormSchema),
    defaultValues: {
      name: initialValues?.name || "",
      description: initialValues?.description || "",
    },
  });

  // Handle form submission
  const handleSubmit = (values: GeofenceFormValues) => {
    const geofenceInput: GeofenceInput = {
      ...values,
      geometry: geofenceGeometry,
      type: geometryType,
    };
    
    onSubmit(geofenceInput);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Geofence Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter geofence name" {...field} />
              </FormControl>
              <FormDescription>
                A descriptive name for this geofence.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter geofence description"
                  className="min-h-[100px]"
                  {...field}
                />
              </FormControl>
              <FormDescription>
                Optional details about this geofence.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            "Save Geofence"
          )}
        </Button>
      </form>
    </Form>
  );
} 